import { m } from 'framer-motion'
import { Inbox } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { useRovingGroup } from '@/shared/hooks/useRovingGroup'
import { dropdownVariants } from '@/shared/motion'

import {
  useMarkAsReadMutation,
  useNotificationsListQuery,
  useUnreadCountQuery,
} from '../hooks/useNotifications'
import { initialNotificationTab, NOTIFICATION_TABS } from '../lib/notificationTabs'
import { notificationTarget, notificationTargetState } from '../lib/notificationTarget'
import type { NotificationResponse, NotificationTab } from '../types/notification.types'

import { DeleteAllReadButton } from './DeleteAllReadButton'
import { MarkAllAsReadButton } from './MarkAllAsReadButton'
import { NotificationList } from './NotificationList'
import { NotificationTabs } from './NotificationTabs'

interface NotificationDropdownProps {
  /** Cierra el dropdown desde fuera (al hacer click en un item). */
  onClose: () => void
}

/**
 * Popover del bell. Lista lazy (solo se monta cuando está abierto). Click en
 * un item → marca leída + navega. "Ver todas" sigue a `/notifications`
 * (placeholder — futura página, hoy 404 hasta que se implemente).
 *
 * Escape + click fuera los gestiona el caller a nivel de container,
 * combinando este dropdown con el botón disparador.
 */
export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)
  const { data: unreadData } = useUnreadCountQuery()
  // Se abre en «Tus partidas», salvo que ahí no haya nada sin leer y en Novedades sí (R3).
  const [tab, setTab] = useState<NotificationTab>(() => initialNotificationTab(unreadData))
  const roving = useRovingGroup({
    items: NOTIFICATION_TABS,
    value: tab,
    onChange: setTab,
    kind: 'tab',
    label: t('notifications.tabs.label'),
  })
  // Novedades se agrupa por tipo: con más avisos por página, los grupos dicen más.
  const { data, isLoading, isError } = useNotificationsListQuery({
    enabled: true,
    tab,
    size: tab === 'NEWS' ? 30 : 10,
  })
  const markAsRead = useMarkAsReadMutation()

  const activityUnread = unreadData?.activity ?? unreadData?.count ?? 0
  const newsUnread = unreadData?.news ?? 0
  const unread = tab === 'NEWS' ? newsUnread : activityUnread
  const totalItems = data?.content.length ?? 0
  // "Limpiar leídas" tiene algo que hacer solo cuando existen items leídos —
  // si todo en pantalla está sin leer, no hay nada que limpiar.
  const noReadItems = totalItems === 0 || unread >= totalItems

  // Foco al primer aviso al abrir — mejora navegabilidad por teclado.
  useEffect(() => {
    panelRef.current?.querySelector<HTMLButtonElement>('li button')?.focus()
  }, [])

  function handleSelect(notification: NotificationResponse) {
    if (notification.readAt === null) {
      markAsRead.mutate(notification.id)
    }
    onClose()
    const target = notificationTarget(notification)
    if (target) void navigate(target, { state: notificationTargetState(notification) })
  }

  return (
    <m.div
      // `dialog` (no `menu`): su contenido son botones de cabecera + una lista,
      // no `menuitem`s — `menu` prometía navegación con flechas que no existe.
      role="dialog"
      variants={dropdownVariants}
      initial="hidden"
      animate="visible"
      aria-label={t('notifications.menuLabel')}
      // En mobile (<sm): fixed al viewport con inset-x-3 — escapa del container
      // del bell. Antes desbordaba por la izquierda porque el bell no está al
      // borde derecho del header (lleva el toggle ES/EN a su lado), y
      // `absolute right-0` anclaba el dropdown justo donde termina el bell,
      // extendiendo 320px de width hacia la izquierda fuera del viewport.
      // En sm+ vuelve al anclaje original right-0 (cabe sin desbordar).
      className="brutal fixed inset-x-3 top-[68px] z-50 origin-top overflow-hidden rounded-xl bg-background sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 sm:origin-top-right"
    >
      <header className="flex items-center justify-between gap-2 border-b-2 border-foreground/10 px-4 py-3">
        <p className="font-brutal text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          {t('notifications.title')}
        </p>
        {/* Ambos botones SIEMPRE se renderizan para que sus posiciones sean
            estables — disabled cuando no aplica. Antes desaparecían al click
            y el botón restante saltaba de posición. */}
        <div className="flex items-center gap-2">
          <MarkAllAsReadButton size="sm" disabled={unread === 0} tab={tab} />
          <DeleteAllReadButton size="sm" disabled={noReadItems} tab={tab} />
        </div>
      </header>

      <NotificationTabs
        roving={roving}
        active={tab}
        activityUnread={activityUnread}
        newsUnread={newsUnread}
      />

      <div ref={panelRef} {...roving.getPanelProps(tab)} className="focus-visible:outline-none">
        {isLoading && (
          <div className="px-4 py-6 text-center font-brutal text-[11px] text-muted-foreground">
            {t('common.loading')}
          </div>
        )}

        {isError && (
          <div className="px-4 py-6 text-center font-brutal text-[11px] text-red">
            {t('common.error')}
          </div>
        )}

        {data && data.content.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <Inbox size={24} className="text-muted-foreground" aria-hidden="true" />
            <p className="font-brutal text-[11px] text-muted-foreground">
              {t(
                tab === 'NEWS' ? 'notifications.emptyTab.NEWS' : 'notifications.emptyTab.ACTIVITY',
              )}
            </p>
          </div>
        )}

        {data && data.content.length > 0 && (
          <NotificationList
            notifications={data.content}
            grouped={tab === 'NEWS'}
            onSelect={handleSelect}
            className="max-h-96 overflow-y-auto overscroll-contain"
          />
        )}
      </div>

      <footer className="border-t-2 border-foreground/10">
        <Link
          to="/notifications"
          onClick={onClose}
          className="block px-4 py-2.5 text-center font-brutal text-[10px] font-bold uppercase tracking-widest text-foreground hover:bg-yellow/30"
        >
          {t('notifications.viewAll')}
        </Link>
      </footer>
    </m.div>
  )
}
